import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Paper,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { memo } from "react";

/**
 * DrawingConfirmationDialog Component - Confirms drawing operations and shows content preview
 * Shows preview of how selected words will be redistributed among cells
 * Extracted from main PdfTableViewer to improve maintainability
 */
const DrawingConfirmationDialog = ({
  // Dialog state
  open,
  onClose,

  // Drawing data
  pendingShapeWords,
  selectedTable,

  // Event handlers
  onConfirm,
}) => {
  const theme = useTheme();
  return (
    <Dialog open={open} onClose={onClose} maxWidth={"md"} fullWidth>
      <DialogTitle>{"Confirm Changes"}</DialogTitle>
      <DialogContent>
        <Typography variant={"body1"} sx={{ mb: 2 }}>
          {`You have selected ${pendingShapeWords.length} words from the PDF. The selected words will be combined into one cell, and removed from their original cells.`}
        </Typography>

        <Typography variant={"body2"} color={"text.secondary"} sx={{ mb: 1 }}>
          {"Combined Text:"}
        </Typography>
        <Paper
          sx={{
            p: 2,
            backgroundColor: "grey.50",
            border: "1px solid",
            borderColor: "grey.300",
            mb: 2,
          }}
        >
          <Typography variant={"body2"} sx={{ fontFamily: "monospace" }}>
            {pendingShapeWords
              .map(({ word }) => word.text)
              .join(" ")
              .trim() || "(Empty)"}
          </Typography>
        </Paper>

        {pendingShapeWords.length > 0 && (
          <>
            <Typography
              variant={"body2"}
              color={"text.secondary"}
              sx={{ mb: 2 }}
            >
              {"Cell content preview after changes:"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                mb: 2,
              }}
            >
              {(() => {
                const cellGroups = {};
                pendingShapeWords.forEach(({ cellKey, word }) => {
                  if (!cellGroups[cellKey]) {
                    cellGroups[cellKey] = [];
                  }
                  cellGroups[cellKey].push(word.text);
                });

                const combinedText = pendingShapeWords
                  .map(({ word }) => word.text)
                  .join(" ")
                  .trim();

                return Object.entries(cellGroups).map(
                  ([cellKey, selectedWords]) => {
                    const [row, col] = cellKey.split("_");
                    const isTarget = cellKey === Object.keys(cellGroups)[0];

                    // Calculate what will remain in this cell
                    let finalCellContent = "";
                    const cellLocationData = selectedTable?.[cellKey];

                    if (
                      cellLocationData &&
                      Array.isArray(cellLocationData) &&
                      cellLocationData[0]?.words
                    ) {
                      const allWords = cellLocationData[0].words;
                      const selectedWordIndices = pendingShapeWords
                        .filter((sw) => sw.cellKey === cellKey)
                        .map((sw) => sw.wordIndex);

                      if (isTarget) {
                        // Target cell gets combined text
                        finalCellContent = combinedText;
                      } else {
                        // Source cells get remaining words
                        const remainingWords = allWords
                          .filter(
                            (_, index) => !selectedWordIndices.includes(index)
                          )
                          .map((word) => word.text);
                        finalCellContent = remainingWords.join(" ").trim();
                      }
                    }

                    return (
                      <Paper
                        key={cellKey}
                        sx={{
                          p: 2,
                          backgroundColor: isTarget ? "primary.50" : "grey.50",
                          border: isTarget ? "2px solid" : "1px solid",
                          borderColor: isTarget ? "primary.main" : "grey.300",
                          borderRadius: "8px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant={"subtitle2"}
                            sx={{
                              fontWeight: "bold",
                              color: isTarget ? "primary.main" : "text.primary",
                            }}
                          >
                            {`Cell ${row},${col}`}
                          </Typography>
                          <Chip
                            label={isTarget ? "TARGET" : "SOURCE"}
                            size={"small"}
                            color={isTarget ? "primary" : "default"}
                            variant={isTarget ? "filled" : "outlined"}
                          />
                        </Box>

                        <Typography
                          variant={"body2"}
                          color={"text.secondary"}
                          sx={{ mb: 1 }}
                        >
                          {isTarget
                            ? `Will contain combined text from ${pendingShapeWords.length} selected words:`
                            : finalCellContent
                            ? `Will contain remaining text after removing ${selectedWords.length} words:`
                            : `Will become empty after removing ${selectedWords.length} words:`}
                        </Typography>

                        <Paper
                          sx={{
                            p: 1.5,
                            backgroundColor: theme.brand.white,
                            border: "1px solid",
                            borderColor: "grey.200",
                            borderRadius: "4px",
                            minHeight: "40px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant={"body2"}
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "13px",
                              color: finalCellContent
                                ? "text.primary"
                                : "text.disabled",
                              fontStyle: finalCellContent ? "normal" : "italic",
                            }}
                          >
                            {finalCellContent || "(Empty)"}
                          </Typography>
                        </Paper>
                      </Paper>
                    );
                  }
                );
              })()}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disableElevation
          variant={"contained"}
          color={"secondary"}
        >
          {"Cancel"}
        </Button>
        <Button onClick={onConfirm} variant={"contained"} color={"primary"}>
          {"Apply Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(DrawingConfirmationDialog);
